import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckSquare, ExternalLink } from 'lucide-react';
import { SEO } from '../../../components/SEO';

const TICKED_TEAL = '#0D9488';

/* â”€â”€â”€ Shared sub-components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
const TickedTopBar = () => (
  <div
    className="sticky top-0 z-50 border-b border-[#E2E8F0]"
    style={{ background: 'rgba(248,247,244,0.95)', backdropFilter: 'blur(20px)' }}
  >
    <div className="container-custom py-4 flex items-center justify-between">
      <Link
        to="/apps/ticked"
        className="flex items-center gap-2 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors group"
      >
        <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-200" />
        Back to Ticked
      </Link>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 flex items-center justify-center rounded-sm" style={{ backgroundColor: TICKED_TEAL }}>
          <CheckSquare size={15} color="#fff" />
        </div>
        <span className="font-display text-[1.2rem] font-bold text-[#0F172A]">Ticked</span>
      </div>
      <a
        href="#"
        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: '#95BF47' }}
      >
        Add to Shopify
        <ExternalLink size={12} />
      </a>
    </div>
  </div>
);

const TickedFooter = () => (
  <div className="border-t border-[#E2E8F0] py-6">
    <div className="container-custom flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] uppercase">
        © {new Date().getFullYear()} Ticked · A Triple A Solutions Product
      </p>
      <div className="flex items-center gap-4">
        <Link to="/apps/ticked/terms" className="font-mono text-[0.55rem] tracking-widest text-[#9CA3AF] hover:text-[#374151] uppercase transition-colors">Terms</Link>
        <span className="text-[#E2E8F0]">·</span>
        <Link to="/apps/ticked/privacy" className="font-mono text-[0.55rem] tracking-widest uppercase" style={{ color: TICKED_TEAL }}>Privacy</Link>
      </div>
    </div>
  </div>
);

const Section = ({ num, title, id, children }) => (
  <motion.div
    id={id}
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
  >
    <div className="flex items-start gap-4 mt-12 mb-3">
      <span className="font-mono text-[0.6rem] tracking-widest uppercase shrink-0 mt-1.5" style={{ color: TICKED_TEAL }}>{num}</span>
      <h2 className="font-display text-xl font-bold text-[#0F172A]">{title}</h2>
    </div>
    <div className="pl-8">{children}</div>
  </motion.div>
);

const P = ({ children }) => (
  <p className="text-[#374151] text-sm leading-relaxed mb-3">{children}</p>
);

const SubHeading = ({ children }) => (
  <h3 className="font-mono text-[0.65rem] tracking-widest text-[#374151] uppercase mb-2 mt-5">{children}</h3>
);

const DataPoint = ({ children }) => (
  <li className="text-[#374151] text-sm leading-relaxed flex items-start gap-2.5">
    <span className="w-1 h-1 rounded-full shrink-0 mt-2" style={{ backgroundColor: TICKED_TEAL }} />
    {children}
  </li>
);

/* â”€â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */
export const TickedPrivacyPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7F4] flex flex-col">
      <SEO
        title="Ticked Privacy Policy"
        description="Privacy policy for the Ticked Shopify app by Smart Solutions."
        path="/apps/ticked/privacy"
      />
      <TickedTopBar />

      <div className="flex-1">
        <div className="container-custom py-16 max-w-3xl mx-auto">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">Legal</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h1
              className="font-display font-bold text-[#0F172A] mb-3"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              Privacy Policy
            </h1>
            <p className="font-mono text-[0.65rem] tracking-widest text-[#9CA3AF] uppercase mb-8">
              Last updated: March 21, 2026
            </p>
            <div className="divider-gold opacity-40" />
          </motion.div>

          {/* Quick jump */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-8 bg-white border border-[#E2E8F0] p-6"
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
          >
            <p className="font-mono text-[0.6rem] tracking-widest text-[#9CA3AF] uppercase mb-3">Jump to section</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {[
                ['#intro', 'Who We Are'],
                ['#data-collected', 'Data We Collect'],
                ['#data-use', 'How We Use Data'],
                ['#legal-basis', 'Legal Basis'],
                ['#data-sharing', 'Data Sharing'],
                ['#retention', 'Retention'],
                ['#security', 'Security'],
                ['#rights', 'Your Rights'],
                ['#cookies', 'Cookies'],
                ['#contact', 'Contact'],
              ].map(([href, label]) => (
                <a key={href} href={href} className="text-xs hover:underline" style={{ color: TICKED_TEAL }}>{label}</a>
              ))}
            </div>
          </motion.div>

          {/* Sections */}
          <Section num="01" title="Introduction & Who We Are" id="intro">
            <P>
              This Privacy Policy explains how Triple A Solutions ("we", "us", "our") collects, uses, stores, and
              protects personal data when you use Ticked, our Shopify task management application.
            </P>
            <P>
              We are committed to handling all personal data responsibly and in compliance with applicable data
              protection laws, including the General Data Protection Regulation (GDPR) and the California Consumer
              Privacy Act (CCPA) where applicable.
            </P>
            <P>
              For any privacy-related questions, contact us at:{' '}
              <a href="mailto:hello@smartsolution.ai" style={{ color: TICKED_TEAL }} className="hover:underline">hello@smartsolution.ai</a>
            </P>
          </Section>

          <Section num="02" title="What Data We Collect" id="data-collected">
            <SubHeading>Shop Data</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>Shopify store ID and domain (collected at the time of OAuth installation)</DataPoint>
            </ul>

            <SubHeading>Staff Data</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>Staff member names</DataPoint>
              <DataPoint>Email addresses (optional — only if provided by the merchant)</DataPoint>
              <DataPoint>Role (Admin or Staff)</DataPoint>
              <DataPoint>Active/inactive status</DataPoint>
              <DataPoint>PIN codes (stored with industry-standard security practices)</DataPoint>
            </ul>

            <SubHeading>Task Data</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>Task titles and descriptions</DataPoint>
              <DataPoint>Priority level, status, due dates, and labels</DataPoint>
              <DataPoint>Checklist items and completion status</DataPoint>
              <DataPoint>Linked Shopify order IDs and order names</DataPoint>
              <DataPoint>Assignee information (linked to staff profiles)</DataPoint>
            </ul>

            <SubHeading>Comments & Audit Logs</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>Text comments left by staff members on tasks</DataPoint>
              <DataPoint>Audit log records: who performed an action, what action was taken, and when</DataPoint>
            </ul>

            <SubHeading>Session Data</SubHeading>
            <ul className="space-y-2 mb-4">
              <DataPoint>Secure session cookies for staff sessions (12-hour expiry)</DataPoint>
            </ul>
          </Section>

          <Section num="03" title="How We Use Your Data" id="data-use">
            <P>We use the data we collect solely to provide and improve the Ticked application. Specifically:</P>
            <ul className="space-y-2 mb-4">
              <DataPoint>To authenticate merchant and staff access to the App</DataPoint>
              <DataPoint>To create, assign, and manage tasks and their associated data</DataPoint>
              <DataPoint>To link tasks to Shopify orders for fulfilment and operational tracking</DataPoint>
              <DataPoint>To generate audit logs for accountability and compliance purposes</DataPoint>
              <DataPoint>To display team dashboards and activity feeds within the App</DataPoint>
              <DataPoint>To maintain session security for staff members using PIN-based login</DataPoint>
            </ul>
            <P>
              We do not use your data or your staff's data for advertising, profiling, or any purpose outside of
              providing the App's functionality.
            </P>
          </Section>

          <Section num="04" title="Legal Basis for Processing" id="legal-basis">
            <P>
              We process personal data under the following legal bases (where GDPR applies):
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint><strong className="font-medium text-[#0F172A]">Contract:</strong> Processing is necessary to deliver the App services you have subscribed to via the Shopify App Store.</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Legitimate Interests:</strong> Processing audit logs and activity records is in our legitimate interest and yours for accountability, security, and service improvement.</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Consent:</strong> Where staff email addresses are voluntarily provided, this is done with the consent of the merchant acting as the data controller for their staff's data.</DataPoint>
            </ul>
          </Section>

          <Section num="05" title="Data Sharing — Third Parties" id="data-sharing">
            <P>
              We do not sell, rent, or trade your data or your staff's data to any third party.
            </P>
            <P>
              Data is shared only with the following sub-processors, solely to operate the App:
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint><strong className="font-medium text-[#0F172A]">Shopify:</strong> The App integrates with Shopify via OAuth to authenticate the merchant and read order data. Shopify's use of data is governed by Shopify's own privacy policies.</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Microsoft Azure:</strong> Our application and database infrastructure is hosted on Azure. Data is stored securely within Azure's cloud infrastructure.</DataPoint>
            </ul>
            <P>
              No data is shared with advertisers, analytics providers, or any other third parties.
            </P>
          </Section>

          <Section num="06" title="Shopify API Data Use">
            <P>
              The App uses Shopify's API solely to: (1) authenticate merchant installation via OAuth; and
              (2) read Shopify order names and IDs so that merchants can link tasks to specific orders.
            </P>
            <P>
              The App does not modify, delete, or write to any Shopify orders. All Shopify API data is used
              exclusively to provide the App's functionality and is handled in accordance with Shopify's
              API Terms of Service and Partner Program Agreement.
            </P>
          </Section>

          <Section num="07" title="Data Retention" id="retention">
            <P>
              Data is retained for as long as the App is installed on your Shopify store. When you uninstall
              the App, your data [RETENTION POLICY — to be filled. Example: will be retained for 30 days and
              then permanently deleted from our systems].
            </P>
            <P>
              Merchants may request deletion of all their store's data at any time by contacting us at{' '}
              <a href="mailto:hello@smartsolution.ai" style={{ color: TICKED_TEAL }} className="hover:underline">hello@smartsolution.ai</a>.
              We will process verified deletion requests within 30 days.
            </P>
          </Section>

          <Section num="08" title="Security" id="security">
            <P>
              We take the security of your data seriously. Our security measures include:
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint>All data is transmitted over HTTPS/TLS</DataPoint>
              <DataPoint>Staff PIN codes are stored with industry-standard security practices</DataPoint>
              <DataPoint>Staff sessions expire after 12 hours automatically</DataPoint>
              <DataPoint>Our Azure infrastructure is protected by cloud-level security controls, access policies, and monitoring</DataPoint>
              <DataPoint>OAuth tokens for Shopify integration are stored securely and never exposed to end users</DataPoint>
            </ul>
            <P>
              While we take all reasonable precautions, no method of transmission or storage is 100% secure.
              If you believe your account has been compromised, contact us immediately.
            </P>
          </Section>

          <Section num="09" title="Your Rights" id="rights">
            <P>
              Depending on your location, you may have the following rights regarding your personal data:
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint><strong className="font-medium text-[#0F172A]">Access:</strong> Request a copy of the personal data we hold about you.</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Rectification:</strong> Request that inaccurate data be corrected.</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Erasure:</strong> Request deletion of your personal data ("right to be forgotten").</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Portability:</strong> Request your data in a machine-readable format.</DataPoint>
              <DataPoint><strong className="font-medium text-[#0F172A]">Objection:</strong> Object to processing based on legitimate interests.</DataPoint>
            </ul>
            <P>
              Note: The merchant is the data controller for their staff's personal data stored in the App.
              Staff members should contact their employer (the merchant) in the first instance regarding data
              rights. Merchants may contact us at{' '}
              <a href="mailto:hello@smartsolution.ai" style={{ color: TICKED_TEAL }} className="hover:underline">hello@smartsolution.ai</a>{' '}
              to exercise rights on behalf of their store.
            </P>
          </Section>

          <Section num="10" title="Cookies & Sessions" id="cookies">
            <P>
              The App uses secure, HTTP-only session cookies to maintain staff login sessions. These cookies:
            </P>
            <ul className="space-y-2 mb-4">
              <DataPoint>Expire automatically after 12 hours</DataPoint>
              <DataPoint>Are not used for tracking or advertising</DataPoint>
              <DataPoint>Are not accessible to third-party scripts</DataPoint>
              <DataPoint>Are required for the App to function — the App cannot be used without session cookies enabled</DataPoint>
            </ul>
          </Section>

          <Section num="11" title="Children's Privacy">
            <P>
              Ticked is a business-facing application intended for use by Shopify merchants and their adult staff
              members. The App is not directed at or intended for use by individuals under the age of 13. We do
              not knowingly collect personal data from children.
            </P>
          </Section>

          <Section num="12" title="Changes to This Policy">
            <P>
              We may update this Privacy Policy from time to time to reflect changes in our practices, legal
              requirements, or service features. We will notify merchants of material changes via the Shopify
              admin or by email. The "last updated" date at the top of this page will always reflect the most
              recent revision.
            </P>
            <P>
              Continued use of the App after an updated Privacy Policy is posted constitutes your acceptance
              of the changes.
            </P>
          </Section>

          <Section num="13" title="Contact Us" id="contact">
            <P>
              For any privacy-related questions, requests, or concerns, please contact us:
            </P>
            <div className="bg-white border border-[#E2E8F0] p-5 mt-2" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <p className="text-sm font-medium text-[#0F172A]">Smart Solutions</p>
              <p className="text-sm text-[#374151] mt-1">
                <a href="mailto:hello@smartsolution.ai" style={{ color: TICKED_TEAL }} className="hover:underline">
                  hello@smartsolution.ai
                </a>
              </p>
              <p className="text-sm text-[#374151]">Kuwait City, Kuwait</p>
            </div>
          </Section>

          <div className="divider-gold opacity-30 mt-16" />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-6">
            <p className="font-mono text-[0.6rem] tracking-widest text-[#9CA3AF] uppercase">
              Last updated: March 21, 2026
            </p>
            <Link
              to="/apps/ticked/terms"
              className="font-mono text-[0.6rem] tracking-widest hover:underline uppercase"
              style={{ color: TICKED_TEAL }}
            >
              View Terms &amp; Conditions →
            </Link>
          </div>
        </div>
      </div>

      <TickedFooter />
    </div>
  );
};
